'use client'

import { useEffect, useMemo } from 'react'
import { Dialog, Button, Divider, Navigation, InfoBanner, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useFormContext } from 'react-hook-form'

import { v4 as uuid } from 'uuid'

import { useTranslate } from '@marketplace-web/shared/i18n'

import { CurrencyAmountModel } from 'types/models/currency-amount'
import {
  SellerOfferPriceSuggestionModel,
  OfferPriceSuggestionOption,
} from '@marketplace-web/domain/conversation-messaging-data'

import { SERVICE_FEE_HELP_URL } from 'constants/routes'

import { useTracking } from '@marketplace-web/shared/event-tracker'

import { sellerMakeOfferStartEvent } from '_libs/common/event-tracker/events'

import OfferSubmitButton from '../OfferSubmitButton/OfferSubmitButton'
import { OfferPriceField } from '../fields/OfferPriceField'
import ItemSummary from '../ItemSummary'

import { OfferFormModel } from '../types'
import SellerOfferPriceSuggestions from './SellerOfferPriceSuggestions/SellerOfferPriceSuggestions'

type Props = {
  show: boolean
  minItemPrice?: CurrencyAmountModel
  maxItemPrice?: CurrencyAmountModel
  thresholdPrice: string | null
  isSubmitting: boolean
  itemPrice: CurrencyAmountModel
  offerPriceSuggestions?: Array<SellerOfferPriceSuggestionModel> | null
  selectedOfferPriceSuggestion?: OfferPriceSuggestionOption
  setSelectedOfferPriceSuggestion: (suggestion: OfferPriceSuggestionOption) => void
  onSubmit: (formValues: OfferFormModel, offerSessionId: string) => void
  onClose: () => void
  itemId: number
  itemTitle?: string
  itemThumbnailUrl?: string | null
  orderItemIds?: Array<number>
  transactionId?: number
}

const SellerOfferDialog = ({
  show,
  onClose,
  onSubmit,
  itemPrice,
  isSubmitting,
  minItemPrice,
  maxItemPrice,
  thresholdPrice,
  itemId,
  itemTitle,
  itemThumbnailUrl,
  orderItemIds,
  offerPriceSuggestions,
  selectedOfferPriceSuggestion,
  setSelectedOfferPriceSuggestion,
  transactionId,
}: Props) => {
  const translate = useTranslate('conversation.hero_message.make_offer')
  const { track } = useTracking()
  const { handleSubmit } = useFormContext<OfferFormModel>()

  const isOfferPriceSuggestionEnabled = !!offerPriceSuggestions?.length
  const thresholdBannerKey = 'offer_modal.threshold_banner_title.seller'
  const offerSessionId = useMemo(() => uuid(), [])

  function renderNavigation() {
    return (
      <Navigation
        body={translate('offer_modal.title')}
        right={
          <Button
            inverse
            inline
            iconName={X24}
            testId="offer-modal-navigation-close-button"
            onClick={onClose}
          />
        }
        testId="offer-modal-navigation"
      />
    )
  }

  const handleFormSubmit = (formValues: OfferFormModel) => onSubmit(formValues, offerSessionId)

  useEffect(() => {
    if (!show) return

    track(
      sellerMakeOfferStartEvent({
        offerSessionId,
        hasSuggestedPrices: isOfferPriceSuggestionEnabled,
        currencyCode: itemPrice.currencyCode,
        currentPrice: Number(parseFloat(itemPrice.amount).toFixed(2)),
        transactionId: transactionId?.toString(),
      }),
    )
  }, [
    isOfferPriceSuggestionEnabled,
    itemPrice.amount,
    itemPrice.currencyCode,
    offerSessionId,
    track,
    show,
    transactionId,
  ])

  return (
    <Dialog show={show} testId="offer-modal">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {renderNavigation()}
        <Divider />
        {thresholdPrice && !isOfferPriceSuggestionEnabled && (
          <InfoBanner
            body={
              <Text
                as="span"
                text={translate(thresholdBannerKey, {
                  amount: thresholdPrice,
                  'learn-more': chunks => (
                    <a href={SERVICE_FEE_HELP_URL} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                })}
                html
              />
            }
            type={InfoBanner.Type.Info}
          />
        )}
        <ItemSummary
          price={itemPrice}
          title={itemTitle}
          thumbnailUrl={itemThumbnailUrl}
          itemCount={orderItemIds?.length}
        />
        {isOfferPriceSuggestionEnabled && selectedOfferPriceSuggestion ? (
          <SellerOfferPriceSuggestions
            minItemPrice={minItemPrice}
            maxItemPrice={maxItemPrice}
            currentItemPrice={itemPrice}
            offerPriceSuggestions={offerPriceSuggestions}
            selectedOfferPriceOption={selectedOfferPriceSuggestion}
            setSelectedOfferPriceSuggestion={setSelectedOfferPriceSuggestion}
            itemId={itemId}
            orderItemIds={orderItemIds}
          />
        ) : (
          <OfferPriceField
            minItemPrice={minItemPrice}
            maxItemPrice={maxItemPrice}
            currentItemPrice={itemPrice}
            itemId={itemId}
            orderItemIds={orderItemIds}
          />
        )}
        {thresholdPrice && isOfferPriceSuggestionEnabled && (
          <InfoBanner
            body={
              <Text
                as="span"
                text={translate(thresholdBannerKey, {
                  amount: thresholdPrice,
                  'learn-more': chunks => (
                    <a href={SERVICE_FEE_HELP_URL} target="_blank" rel="noreferrer">
                      {chunks}
                    </a>
                  ),
                })}
                html
              />
            }
            type={InfoBanner.Type.Info}
          />
        )}
        <OfferSubmitButton
          currency={itemPrice.currencyCode}
          isSubmitting={isSubmitting}
          isOfferPriceDisplayEnabled={isOfferPriceSuggestionEnabled}
        />
      </form>
    </Dialog>
  )
}

export default SellerOfferDialog
